import { useEffect, useMemo } from 'react';

import {
  StringIndexedObject,
  Widget,
  useFormContext,
  useWatch,
} from '@kanda-libs/ks-component-ts';

import { ERROR_PROPS, PROPS } from './constants';

export default function useBankFields() {
  const { setValue, formState } = useFormContext();

  const [years, months] = useWatch({
    name: [PROPS.accountYearsOwned.name, PROPS.accountMonthsOwned.name],
  });

  const timeValidation = {
    ...Widget.CreditBankAccountMonthsHeldValidation,
    required: {
      value: true,
      message:
        'You must provide the amount of time you have held this bank account for',
    },
  };

  const validatePattern = (sortcode: string) => {
    if (!sortcode) return false;
    const formatted = sortcode?.replace(/undefined/g, '');
    return /^[0-9]{2}-[0-9]{2}-[0-9]{2}$/.test(formatted);
  };

  const validateLength = (sortcode: string) => {
    if (!sortcode) return false;
    return sortcode?.replace(/-/g, '')?.replace(/undefined/g, '')?.length === 6;
  };

  const sortCodeValidation = {
    validate: {
      value: {
        length: (value: string) => validateLength(value),
        pattern: (value: string) => validatePattern(value),
      },
      message: {
        length: 'Sort code must be 6 digits',
        pattern: 'You must enter a valid sort code',
      },
    },
    required: {
      value: true,
      message: 'You must provide your sort code',
    },
  };

  const error = useMemo(() => {
    const heldError = (formState?.errors?.credit as StringIndexedObject)
      ?.bank_account?.months_held;
    if (!heldError) return null;
    if (heldError?.type === 'required') return ERROR_PROPS.label;
    return 'There is an error with this input';
  }, [formState]);

  useEffect(() => {
    if (!years && !months) {
      setValue('credit.bank_account.months_held', undefined);
      return;
    }
    const time = (years || 0) * 12 + (months || 0);
    setValue('credit.bank_account.months_held', time);
  }, [years, months, setValue]);

  return {
    error,
    timeValidation,
    sortCodeValidation,
  };
}
